import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';
import { Sparkles } from '@/utils/icons';

/**
 * Simplified Whoop OAuth callback component for distributed AWS environments.
 */
const WhoopSimpleCallback: React.FC = () => {
    const [step, setStep] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [searchParams] = useSearchParams();

    const steps = [
        'Verifying OAuth response',
        'Exchanging authorization code',
        'Storing tokens',
        'Completing setup'
    ];

    useEffect(() => {
        handleOAuthCallback();
    }, []);

    const handleOAuthCallback = async () => {
        try {
            setStep(0);
            
            // Get URL parameters
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error_param = searchParams.get('error');

            // Check for OAuth errors
            if (error_param) {
                setError(`Whoop OAuth error: ${error_param}`);
                return;
            }

            if (!code || !state) {
                setError('Missing authorization code or state parameter');
                return;
            }

            setStep(1);

            // Get stored OAuth parameters
            const storedState = localStorage.getItem('whoop_oauth_state');
            const codeVerifier = localStorage.getItem('whoop_code_verifier');

            if (!storedState || !codeVerifier) {
                setError('OAuth session expired - please try connecting again');
                return;
            }

            if (storedState !== state) {
                setError('Invalid OAuth state - possible security issue');
                return;
            }

            setStep(2);

            // Get JWT token
            const token = localStorage.getItem('access');
            if (!token) {
                setError('Authentication required - please log in again');
                navigate('/login');
                return;
            }

            // Complete OAuth flow using existing endpoint
            const response = await fetch(getApiUrl('/api/whoop/callback/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code: code,
                    code_verifier: codeVerifier,
                    state: state
                })
            });

            const result = await safeJsonParse(response, 'completing Whoop OAuth callback');

            if (!result.success) {
                throw new Error(result.error || 'Failed to complete OAuth flow');
            }

            setStep(3);

            // Clean up stored OAuth parameters
            localStorage.removeItem('whoop_oauth_state');
            localStorage.removeItem('whoop_code_verifier');

            // Refresh user info from backend to update whoop_connected status
            // Note: token is already available from line 67
            if (token) {
                try {
                    const userInfoResponse = await fetch(getApiUrl('/api/users/me/'), {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                    
                    const userInfoResult = await safeJsonParse(userInfoResponse, 'refreshing user info after Whoop connection');
                    
                    if (userInfoResult.success) {
                        localStorage.setItem('user_info', JSON.stringify(userInfoResult.data));
                    }
                } catch (err) {
                    console.error('Failed to refresh user info:', err);
                    // Non-critical error, continue with redirect
                }
            }

            setSuccess(true);

            // Redirect to dashboard after success
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (error: any) {
            console.error('OAuth callback error:', error);
            setError(error.message || 'Failed to complete Whoop connection');
            
            // Clean up on error
            localStorage.removeItem('whoop_oauth_state');
            localStorage.removeItem('whoop_code_verifier');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 cgm-bg-primary transition-colors duration-300">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <img 
                        src={isDark ? "/logos/whoop/Logo/Wordmark/WHOOP Logo White.svg" : "/logos/whoop/Logo/Wordmark/WHOOP Logo Black.svg"}
                        alt="WHOOP Logo"
                        className="mx-auto mb-6"
                        style={{ minWidth: 100, maxWidth: 200 }}
                    />
                    <h2 className="text-2xl font-light cgm-text-primary mb-2">
                        {success ? 'Connection Successful!' : 'Processing Connection...'}
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        {success ? 'Your Whoop account has been connected successfully.' : 'Completing your Whoop account connection...'}
                    </p>
                </div>

                <div className="cgm-card p-6">
                    {error ? (
                        <div className="text-center">
                            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                                {error}
                            </div>
                            <button
                                onClick={() => navigate('/connect-whoop-simple')}
                                className="cgm-button px-4 py-2"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : success ? (
                        <div className="text-center">
                            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">
                                Whoop account connected successfully! Redirecting to dashboard...
                            </div>
                            <div className="mb-4 flex justify-center">
                                <Sparkles className="w-16 h-16 cgm-text-primary" />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {steps.map((stepText, index) => (
                                <div key={index} className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        index < step 
                                            ? 'bg-green-500 text-white' 
                                            : index === step 
                                                ? 'bg-blue-500 text-white animate-pulse'
                                                : 'bg-gray-300 text-gray-600'
                                    }`}>
                                        {index < step ? '✓' : index + 1}
                                    </div>
                                    <span className={`${
                                        index <= step ? 'cgm-text-primary' : 'cgm-text-muted'
                                    }`}>
                                        {stepText}
                                    </span>
                                </div>
                            ))}
                            
                            <div className="flex justify-center mt-6">
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WhoopSimpleCallback;
